import { Flex, Typography, useSafeArea, useScreenSize } from '@banhala/ui-kit';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolation,
  withTiming,
  useAnimatedReaction,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { useSetTab, useTabRef } from '../atom/tab.atom';
import { AnimatedList } from './AnimatedList';
import { SectionContainer } from './SectionContainer';
import { CoverImages } from './CoverImages';
import { MarketInfoBanner } from './MarketInfoBanner';
import { MumulLiveProfileSection } from './MumulLive/MumulLiveProfileSection';
import { GoodsInfo } from './GoodsInfo';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ExpectedPurchasePrice } from './ExpectedPurchasePrice';
import { GoodsPromotions } from './GoodsPromotions';
import { CouponListBanner } from './CouponListBanner';
import { DeliveryInfo } from './DeliveryInfo';
import { MarketInfo } from './MarketInfo';
import { BrandInfo } from './BrandInfo';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { CategoryInfo } from './CategoryInfo';
import { ReviewSummary } from './ReviewSummary';
import { GoodsCarousel } from './GoodsCarousel';
import { ContentTab } from './ContentTab';
import type { GoodsDetailTab } from '../types/GoodsDetailTab';
import { GoodsContent } from './GoodsContent';
import { FloatingButtons } from './FloatingButtons';
import { BottomDockedButton } from './BottomDocked';
import { AffiliateFloatingButton } from './AffiliateFloating/AffiliateFloatingButton';
import { useAffiliateFloating } from '../hooks/useAffiliateFloating';
import { useLogViewGoodsDetail } from '../hooks/useLogViewGoodsDetail';
import { useLogClickGoodsDetailTab } from '../hooks/useLogClickGoodsDetailTab';
import { LiveCampaignBanner } from './LiveCampaignBanner';
import { useRelatedLiveCampaign } from '../queries/useRelatedLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useSafeTimeout } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedEffect,
} from 'shared/src/hooks/retainedState';
import { useScrollRestoration } from 'shared/src/hooks/useScrollRestoration';
import { useLogGoodsAnalyticsEvent } from '../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { Metadata } from 'shared/src/component';
import {
  getMetaDescription,
  getMetaKeywords,
  getMetaTitle,
  getProductMeta,
} from '../utils/goodsMetaData';
import { isAndroid, isIOS, isNotNil, isWeb } from 'shared/src/utils';
import { usePreloadQuery } from '../hooks/usePreloadQuery';
import type { ListRenderItem } from 'react-native';
import { useGoodsSno } from '../hooks/useGoodsSno';
import type { FlatList } from 'react-native-gesture-handler';
import { useCheckGoodsRedirectDeeplink } from '../queries/useCheckGoodsRedirectDeeplink';

const BOTTOM_DOCKED_BUTTON_HEIGHT = 56;
const BOTTOM_DOCKED_BUTTON_PADDING = 12 * 2;
const LIVE_CAMPAIGN_BANNER_HEIGHT = 48;

// sticky fling 정지의 오버슈트 허용치(px).
// 프레임 드랍으로 한 프레임에 sticky 를 크게 지나친 경우, 절대 위치로 snap 하면
// 이미 내려간 콘텐츠를 sticky 로 강제로 되감아 "내려갔다 다시 올라오는" 튐이 생긴다.
// sticky 를 이 값 이내로 살짝 통과한 경우에만 snap 하고, 크게 지나쳤으면 그대로 진행.
const STICKY_SNAP_MAX_OVERSHOOT = 60;

const scrollOffsetSlot = retainedState(0);

// 모듈 스코프 상수 — 데이터 상태와 무관하게 항상 동일 reference/순서.
const COMPONENT_LIST: GoodsDetailContentKeys[] = [
  'cover',
  'promotions',
  'market',
  'info',
  'expected-purchase-price',
  'coupons',
  'delivery-info',
  'brand-info',
  'mumul-live-profile',
  'review-summary',
  'carousel',
  'content-tab',
  'content',
];

const useLiveCampaignCGroupLogging = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isCGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['C'],
  });
  const logEvent = useLogGoodsAnalyticsEvent();

  useRetainedEffect(
    'view-shoplive-target-goods-detail',
    () => {
      if (!campaign || !isCGroup) return false;

      logEvent({
        name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
        channel: ['braze'],
        analytics: campaign.logging.analytics,
      });
    },
    [campaign, isCGroup, logEvent],
  );
};

export const GoodsDetailContents: React.FC = () => {
  const scrollY = useSharedValue(0);
  const isScrolling = useSharedValue(false);
  const goodsSno = useGoodsSno();

  const tabScrollX = useSharedValue(0);
  const { width, height: screenHeight } = useScreenSize();
  const { bottom } = useSafeArea();
  const setSafeTimeout = useSafeTimeout();
  const listRef = useRef<FlatList<GoodsDetailContentKeys> | null>(null);
  const tabLastScrollYValueRef = useRef<{
    [key in GoodsDetailTab]: number;
  }>({
    INFORMATION: 0,
    REVIEW: 0,
    SIZE: 0,
    INQUIRY: 0,
  });

  const sectionHeightRef = useRef<{ [key: string]: number }>({});
  const stickyItemPositionY = useSharedValue(0);

  const recalcStickyPosition = useCallback(() => {
    const heights = sectionHeightRef.current;
    const nextY = Object.keys(heights)
      .filter(key => key !== 'content' && key !== 'content-tab')
      .reduce((prev, cur) => prev + heights[cur], 0);
    if (stickyItemPositionY.value !== nextY) {
      stickyItemPositionY.value = nextY;
    }
  }, [stickyItemPositionY]);

  const handleChangeHeight = useCallback(
    (key: string, height: number) => {
      if (sectionHeightRef.current[key] === height) return;
      sectionHeightRef.current[key] = height;
      recalcStickyPosition();
    },
    [recalcStickyPosition],
  );

  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: liveCampaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });
  const showLiveBanner = isNotNil(liveCampaign) && isBGroup;

  const tabRef = useTabRef();
  const setTab = useSetTab();
  const logClickGoodsDetailTab = useLogClickGoodsDetailTab();

  useLiveCampaignCGroupLogging();
  usePreloadQuery();
  useCheckGoodsRedirectDeeplink();

  // Android native(CoordinatorLayout + ScrollingAppBarBehavior) 의 동작 정합:
  // fling 중에 sticky 위치(=collapse 종료) 를 지나는 순간 momentum 을 끊어
  // sticky 직후에서 정지시킴. 드래그(손가락 접촉 중) 는 자연스럽게 통과.
  //
  // 조건 설계:
  // - `flingStartScrollY < sticky`: fling 시작이 sticky 아래일 때만 적용 (sticky 위 fling 무시)
  // - `!hasSnappedToSticky`: 이번 fling 에서 1회만 snap (snap 후 native fling 이 안 끊겨도 무한 snap 방지)
  // - `next >= sticky`: 절대 위치 기준이라 frame drop 으로 prev/next 사이를 통과해도 잡힘
  const isFlinging = useSharedValue(false);
  const flingStartScrollY = useSharedValue(0);
  const hasSnappedToSticky = useSharedValue(false);
  const snapToSticky = useCallback(() => {
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionY.value,
      animated: false,
    });
  }, [stickyItemPositionY]);

  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({ offset, animated });
  }, []);

  const scrollRestore = useScrollRestoration({
    token: scrollOffsetSlot,
    scrollTo: scrollToOffset,
    viewportHeight: screenHeight,
  });

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      const next = event.contentOffset.y;
      scrollY.value = next;
      isScrolling.value = true;

      const stickyOffset = stickyItemPositionY.value;
      // 양방향 fling 모두 sticky 통과 시점에 정지.
      // - 아래로 fling (scrollY 증가): 시작 < sticky, 현재 >= sticky
      // - 위로 fling (scrollY 감소): 시작 > sticky, 현재 <= sticky
      // 단, 프레임 드랍으로 sticky 를 STICKY_SNAP_MAX_OVERSHOOT 이상 지나친 경우엔
      // snap 을 건너뛴다(되감으면 튀므로). 살짝 통과한 경우에만 sticky 로 정지.
      const isCrossingDown =
        flingStartScrollY.value < stickyOffset &&
        next >= stickyOffset &&
        next - stickyOffset <= STICKY_SNAP_MAX_OVERSHOOT;
      const isCrossingUp =
        flingStartScrollY.value > stickyOffset &&
        next <= stickyOffset &&
        stickyOffset - next <= STICKY_SNAP_MAX_OVERSHOOT;

      if (
        isAndroid &&
        isFlinging.value &&
        !hasSnappedToSticky.value &&
        stickyOffset > 0 &&
        (isCrossingDown || isCrossingUp)
      ) {
        hasSnappedToSticky.value = true;
        scheduleOnRN(snapToSticky);
      }
    },
    onEndDrag: event => {
      // 손가락을 뗐는데 속도가 거의 없으면(=관성 없음) 즉시 정지로 본다.
      // 속도가 있으면 onMomentum*로 이어지므로 여기서 false 처리하지 않는다.
      if (Math.abs(event.velocity?.y ?? 0) < 0.1) {
        isScrolling.value = false;
        scheduleOnRN(scrollRestore.save, scrollY.value);
      }
    },
    onMomentumBegin: () => {
      isFlinging.value = true;
      isScrolling.value = true;
      flingStartScrollY.value = scrollY.value;
      hasSnappedToSticky.value = false;
    },
    onMomentumEnd: () => {
      isFlinging.value = false;
      isScrolling.value = false;
      hasSnappedToSticky.value = false;
      scheduleOnRN(scrollRestore.save, scrollY.value);
    },
  });

  // sticky overlay 활성 상태 — pointerEvents 제어용.
  // useAnimatedReaction 으로 threshold 교차 시점에만 setState (1프레임 단위 X)
  const [isStickyActive, setIsStickyActive] = useState(false);
  useAnimatedReaction(
    () =>
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value,
    (current, previous) => {
      if (current !== previous) {
        scheduleOnRN(setIsStickyActive, current);
      }
    },
    [scrollY, stickyItemPositionY],
  );

  const stickyOverlayStyle = useAnimatedStyle(() => {
    const active =
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value;
    return {
      opacity: active ? 1 : 0,
    };
  });

  const marginBottom = useMemo(() => {
    const DOCKED_PRIMARY_BUTTON_HEIGHT =
      BOTTOM_DOCKED_BUTTON_HEIGHT + BOTTOM_DOCKED_BUTTON_PADDING;
    return bottom + DOCKED_PRIMARY_BUTTON_HEIGHT;
  }, [bottom]);

  const scrollToStickyView = useCallback(() => {
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionY.value,
      animated: true,
    });
  }, [stickyItemPositionY]);

  const changeContentTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      setTab(nextTab);
      const tabList: GoodsDetailTab[] = [
        'INFORMATION',
        'REVIEW',
        'SIZE',
        'INQUIRY',
      ];
      if (isIOS) {
        tabScrollX.value = withTiming(
          tabList.findIndex(t => t === nextTab),
          { duration: 200 },
        );
      }
    },
    [setTab, tabScrollX],
  );

  const onChangeTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      const prevTab = tabRef.current;
      if (prevTab !== nextTab) {
        logClickGoodsDetailTab(nextTab);
        const currentScrollY = scrollY.value;
        //마지막 스크롤 저장
        tabLastScrollYValueRef.current = {
          ...tabLastScrollYValueRef.current,
          [prevTab]: currentScrollY,
        };

        if (currentScrollY > stickyItemPositionY.value) {
          setSafeTimeout(() => {
            scrollToOffset(
              Math.max(
                tabLastScrollYValueRef.current[nextTab],
                stickyItemPositionY.value,
              ),
              false,
            );
          }, 1);
        }
      }
      setTab(nextTab);
    },
    [
      tabRef,
      setTab,
      logClickGoodsDetailTab,
      scrollY,
      scrollToOffset,
      stickyItemPositionY,
      setSafeTimeout,
    ],
  );

  useLogViewGoodsDetail();

  const { floatingText, showFloating, onViewFloating, onClickFloating } =
    useAffiliateFloating();

  const listStyle = useMemo(() => ({ flex: 1, marginBottom }), [marginBottom]);

  const listContentContainerStyle = useMemo(
    () =>
      showLiveBanner ? { paddingTop: LIVE_CAMPAIGN_BANNER_HEIGHT } : undefined,
    [showLiveBanner],
  );

  const renderItem = useCallback<ListRenderItem<GoodsDetailContentKeys>>(
    ({ item }) => {
      if (item === 'cover') {
        return (
          <SectionContainer
            height={width}
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CoverImages />
          </SectionContainer>
        );
      }

      if (item === 'promotions') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsPromotions />
          </SectionContainer>
        );
      }

      if (item === 'market') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfoBanner />
          </SectionContainer>
        );
      }

      if (item === 'info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsInfo />
          </SectionContainer>
        );
      }

      if (item === 'expected-purchase-price') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ExpectedPurchasePrice />
          </SectionContainer>
        );
      }

      if (item === 'coupons') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CouponListBanner />
          </SectionContainer>
        );
      }

      if (item === 'delivery-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <DeliveryInfo />
          </SectionContainer>
        );
      }

      if (item === 'mumul-live-profile') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MumulLiveProfileSection />
          </SectionContainer>
        );
      }

      if (item === 'market-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfo />
          </SectionContainer>
        );
      }
      if (item === 'brand-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <BrandInfo />
          </SectionContainer>
        );
      }
      if (item === 'review-summary') {
        if (!goodsDetail?.component_visibility.review_summary) return null;
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ReviewSummary />
          </SectionContainer>
        );
      }

      if (item === 'category-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CategoryInfo />
          </SectionContainer>
        );
      }

      if (item === 'carousel') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsCarousel />
          </SectionContainer>
        );
      }

      if (item === 'content-tab') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ContentTab />
          </SectionContainer>
        );
      }

      if (item === 'content') {
        // if (!isAndroid && (isNil(goodsBasic) || isNil(goodsDetail))) {
        //   return null;
        // }
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsContent />
          </SectionContainer>
        );
      }

      return (
        <Flex height={300} backgroundColor={'white'}>
          <Typography>{item}</Typography>
        </Flex>
      );
    },
    [width, handleChangeHeight, goodsDetail],
  );

  const bannerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [0, LIVE_CAMPAIGN_BANNER_HEIGHT],
            [0, -LIVE_CAMPAIGN_BANNER_HEIGHT],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const scrollContextValue = useMemo(
    () => ({
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
    }),
    [
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
    ],
  );

  const stickyOverlayContainerStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      // zIndex: 2,
    }),
    [],
  );

  return (
    <ScrollContext value={scrollContextValue}>
      {isNotNil(goodsBasic) && (
        <Metadata
          title={getMetaTitle(goodsBasic)}
          imgSrc={goodsBasic?.cover_images[0] ?? ''}
          path={`/goods/${goodsBasic.sno}`}
          description={getMetaDescription(goodsBasic)}
          keywords={getMetaKeywords(goodsBasic)}
          productMeta={getProductMeta(goodsBasic)}
          isCanonical
        />
      )}
      {showLiveBanner && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            },
            bannerAnimatedStyle,
          ]}
        >
          <LiveCampaignBanner />
        </Animated.View>
      )}
      <AnimatedList
        ref={listRef}
        {...(isWeb ? { nativeID: 'goods-detail-scroll' } : {})}
        style={[listStyle, scrollRestore.restoring ? { opacity: 0 } : null]}
        contentContainerStyle={[
          listContentContainerStyle,
          isWeb && { maxWidth: width, alignSelf: 'center' },
        ]}
        onScroll={scrollHandler}
        onContentSizeChange={scrollRestore.onContentSizeChange}
        contentInsetAdjustmentBehavior={'never'}
        data={COMPONENT_LIST}
        renderItem={renderItem}
        scrollEventThrottle={1}
      />
      {/* Sticky overlay — content-tab의 자연 위치를 지나면 fade in.
          RN의 stickyHeaderIndices를 쓰지 않고 자체 구현하여
          ScrollViewStickyHeader의 cascading setState 회피. */}
      <Animated.View
        pointerEvents={isStickyActive ? 'auto' : 'none'}
        style={[stickyOverlayContainerStyle, stickyOverlayStyle]}
      >
        <ContentTab />
      </Animated.View>
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        alignItems={'center'}
      >
        <Flex width={width}>
          {/* web에서 해당 key값을 할당 하지 않으면 unmount가 되지 않고 단순히 re-render가됨.
          이전 상품에서 선택했던 옵션을 그대로 보여줍니다.
          key값을 추가해서 새로 받아 올 수 있도록 합니다.
          */}
          <BottomDockedButton key={goodsSno.toString()} />
        </Flex>
      </Flex>
      <FloatingButtons
        bottomOffset={marginBottom}
        scrollToYPosition={isAndroid ? stickyItemPositionY.value : 0}
      />
      {showFloating && (
        <AffiliateFloatingButton
          text={floatingText}
          marginBottom={marginBottom}
          onPress={onClickFloating}
          onView={onViewFloating}
        />
      )}
    </ScrollContext>
  );
};
